import React, { useState, useEffect } from 'react';
import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  FileListSidebar,
  Chat,
  NoDataVisual,
  Button,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { observer } from 'mobx-react';
import { useStores } from '../../stores';
import axios from 'axios';
import properties from '../../core-utils/properties';
import Loader from '../../_components/molecules/Loader';
import {
  initialiseChatWithFile,
  getChatHistory,
  sendChatMessage,
  ChatMessage,
} from '../../core-utils/services/chatService';
import { v4 as uuidv4 } from 'uuid';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { scrollBarStyles } from '../../core-utils/messages/CommonConstants';
import { createBlobUrl } from '../../core-utils/helper-utils/contractAIQHelper';
import { highlightPlugin } from '@react-pdf-viewer/highlight';
import '@react-pdf-viewer/highlight/lib/styles/index.css';
import ReactMarkdown from 'react-markdown';
import { PDF_WORKER_URL } from '../../core-utils/helper-utils/pdfWorker';

const useStyles = makeStyles()({
  root: {
    display: 'flex',
    height: 'calc(100vh - 64px)', // Subtract header height
    width: '100%',
    backgroundColor: patientAccessTheme.palette.background.default,
    position: 'relative',
  },
  sidebar: {
    minWidth: '200px',
    maxWidth: '500px',
    borderRight: `1px solid ${patientAccessTheme.palette.divider}`,
    overflow: 'auto',
    backgroundColor: patientAccessTheme.palette.status.white,
    position: 'relative',
  },
  middleSection: {
    minWidth: '300px',
    maxWidth: '800px',
    borderRight: `1px solid ${patientAccessTheme.palette.divider}`,
    overflow: 'auto',
    backgroundColor: patientAccessTheme.palette.status.white,
    padding: '24px',
    position: 'relative',
    ...scrollBarStyles,
  },
  chatContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minWidth: '400px',
  },
  resizeHandle: {
    width: '4px',
    cursor: 'col-resize',
    backgroundColor: 'transparent',
    position: 'relative',
    flexShrink: 0,
    '&:hover': {
      backgroundColor: patientAccessTheme.palette.primary.main,
    },
    '&:active': {
      backgroundColor: patientAccessTheme.palette.primary.main,
    },
  },
  placeholderText: {
    color: patientAccessTheme.palette.text.secondary,
    fontSize: '16px',
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: '100%',
  },
});

// Markdown Message Component for AI responses
const MarkdownMessage: React.FC<{ content: string }> = ({ content }) => {
  return (
    <ReactMarkdown
      components={{
        // Customize rendering for different markdown elements
        p: ({ node, ...props }: any) => (
          <Typography
            fontSize="14px"
            lineHeight="21px"
            fontType="regular"
            textColor={patientAccessTheme.palette.text.highemphasis}
            margin="0 0 8px 0"
            {...props}
          />
        ),
        h1: ({ node, ...props }: any) => (
          <Typography
            fontSize="20px"
            lineHeight="28px"
            fontType="bold"
            textColor={patientAccessTheme.palette.text.highemphasis}
            margin="16px 0 8px 0"
            {...props}
          />
        ),
        h2: ({ node, ...props }: any) => (
          <Typography
            fontSize="18px"
            lineHeight="26px"
            fontType="bold"
            textColor={patientAccessTheme.palette.text.highemphasis}
            margin="14px 0 8px 0"
            {...props}
          />
        ),
        h3: ({ node, ...props }: any) => (
          <Typography
            fontSize="16px"
            lineHeight="24px"
            fontType="semiBold"
            textColor={patientAccessTheme.palette.text.highemphasis}
            margin="12px 0 8px 0"
            {...props}
          />
        ),
        code: ({ node, inline, ...props }: any) =>
          inline ? (
            <code
              style={{
                backgroundColor: patientAccessTheme.palette.grey[100],
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '13px',
                fontFamily: 'monospace',
              }}
              {...props}
            />
          ) : (
            <pre
              style={{
                backgroundColor: patientAccessTheme.palette.grey[100],
                padding: '12px',
                borderRadius: '8px',
                overflow: 'auto',
                fontSize: '13px',
                fontFamily: 'monospace',
                margin: '8px 0',
              }}
            >
              <code {...props} />
            </pre>
          ),
        ul: ({ node, ...props }: any) => (
          <ul
            style={{
              margin: '8px 0',
              paddingLeft: '24px',
            }}
            {...props}
          />
        ),
        ol: ({ node, ...props }: any) => (
          <ol
            style={{
              margin: '8px 0',
              paddingLeft: '24px',
            }}
            {...props}
          />
        ),
        li: ({ node, ...props }: any) => (
          <li
            style={{
              marginBottom: '4px',
              fontSize: '14px',
              lineHeight: '21px',
              color: patientAccessTheme.palette.text.highemphasis,
            }}
            {...props}
          />
        ),
        strong: ({ node, ...props }: any) => (
          <strong
            style={{
              fontWeight: 600,
              color: patientAccessTheme.palette.text.highemphasis,
            }}
            {...props}
          />
        ),
        a: ({ node, ...props }: any) => (
          <a
            style={{
              color: patientAccessTheme.palette.primary[500],
              textDecoration: 'none',
            }}
            {...props}
          />
        ),
        blockquote: ({ node, ...props }: any) => (
          <blockquote
            style={{
              borderLeft: `4px solid ${patientAccessTheme.palette.primary[500]}`,
              paddingLeft: '16px',
              margin: '8px 0',
              fontStyle: 'italic',
              color: patientAccessTheme.palette.text.mediumemphasis,
            }}
            {...props}
          />
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

const ChatPage: React.FC = observer(() => {
  const { classes } = useStyles();
  const employerGroupStore = useStores().employerGroupStore;
  const contractStore = useStores().contractAiqStore;
  const egNid = employerGroupStore.employerGroupResponseData.eg_nid[0];

  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [files, setFiles] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Chat states
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatLoading, setChatLoading] = useState<boolean>(false);
  const [chatInitializing, setChatInitializing] = useState<boolean>(false);
  const [isThinking, setIsThinking] = useState<boolean>(false);
  const [streamingResponse, setStreamingResponse] = useState<string>('');
  const [threadId] = useState<string>(uuidv4());
  const [chatKey] = useState<string>(uuidv4());
  const [chatSuggestions, setChatSuggestions] = useState<string[]>([]);

  // Resizable panel states
  const [leftPanelWidth, setLeftPanelWidth] = useState<number>(300);
  const [middlePanelWidth, setMiddlePanelWidth] = useState<number>(750);
  const [isResizingLeft, setIsResizingLeft] = useState<boolean>(false);
  const [isResizingRight, setIsResizingRight] = useState<boolean>(false);

  // PDF text selection state
  const [selectedText, setSelectedText] = useState<string>('');
  const [chatInputValue, setChatInputValue] = useState<string>('');

  // Handle adding selected text to chat
  const handleAddToChat = (text: string) => {
    if (!text || !text.trim()) {
      return;
    }

    setSelectedText(text);
    // Add the selected text to the chat input
    const formattedText = `${chatInputValue} "${text}"`;
    setChatInputValue(formattedText);
  };

  // Handle suggestion click - add to input instead of sending
  const handleSuggestionClick = (suggestion: string) => {
    setChatInputValue(suggestion);
  };

  // Highlight plugin for PDF text selection
  const renderHighlightTarget = (props: any) => (
    <div
      style={{
        background: '#fff',
        border: '1px solid rgba(0, 0, 0, .3)',
        borderRadius: '15px',
        padding: '8px',
        position: 'absolute',
        left: `${props.selectionRegion.left}%`,
        top: `${props.selectionRegion.top + props.selectionRegion.height}%`,
        transform: 'translate(0, 8px)',
        zIndex: 1000,
      }}
    >
      <Button
        label="Ask AI"
        variant="contained"
        color="primary"
        height="32px"
        width="fit-content"
        onClick={() => {
          const selectedTextContent = props.selectedText || '';
          handleAddToChat(selectedTextContent);
          props.cancel();
        }}
      />
    </div>
  );

  const highlightPluginInstance = highlightPlugin({
    renderHighlightTarget,
  });

  // Fetch contracts
  const getContracts = async () => {
    try {
      const response = await axios.post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.getContracts,
        { eg_nid: egNid },
      );

      if (response.status === 200) {
        const data = response?.data?.data || [];
        const contractFiles: any[] = [];

        data.forEach((contract: any) => {
          // Add all documents from each contract
          contract.documents?.forEach((doc: any) => {
            const fileItem = {
              id: doc._id,
              name: doc.document_name,
              type: 'contract',
              size: '-',
              lastModified: new Date(doc.document_effective_date || Date.now()),
              group: 'Contracts',
              category: 'Contracts',
              contractName: contract.health_care_partner_name,
              serviceAgreement: contract.service_agreement_name,
              originalUrl: doc.original_url || doc.document_url,
              url: '', // Will be populated with blob URL when selected
            };
            contractFiles.push(fileItem);
          });
        });

        return contractFiles;
      }
      return [];
    } catch (error) {
      console.error('Error fetching contracts:', error);
      return [];
    }
  };

  // Fetch plan documents
  const getPlanDocuments = async () => {
    try {
      const response = await axios.post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.getPlanDocs,
        { eg_nid: egNid },
      );

      if (response.status === 200) {
        const data = response?.data?.data || [];
        const planFiles: any[] = [];

        data.forEach((plan: any) => {
          // Add all documents from each plan
          plan.documents?.forEach((doc: any) => {
            const fileItem = {
              id: doc._id,
              name: doc.document_name,
              type: 'plan',
              size: '-',
              lastModified: new Date(doc.document_effective_date || Date.now()),
              group: 'Plan Documents',
              category: 'Plan Documents',
              documentType: plan.document_type,
              benefitTypes: plan.benefit_types?.join(', ') || 'Medical',
              originalUrl: doc.original_url || doc.document_url,
              url: '', // Will be populated with blob URL when selected
            };
            planFiles.push(fileItem);
          });
        });

        return planFiles;
      }
      return [];
    } catch (error) {
      console.error('Error fetching plan documents:', error);
      return [];
    }
  };

  // Fetch all files on mount
  useEffect(() => {
    const fetchFiles = async () => {
      setLoading(true);
      const [contractFiles, planFiles] = await Promise.all([
        getContracts(),
        getPlanDocuments(),
      ]);

      // Combine and group files
      const allFiles = [...contractFiles, ...planFiles];
      setFiles(allFiles);
      setLoading(false);
    };

    if (egNid) {
      fetchFiles();
    }
  }, [egNid]);

  // Load chat history for selected file
  const loadChatHistory = async (file: any) => {
    if (!file?.url || !file?.id) {
      return;
    }

    // Update file status to loading, clear status from other files
    setFiles((prev) =>
      prev.map((f) => {
        if (f.id === file.id) {
          return { ...f, status: 'inprogress' };
        } else {
          // Remove status from non-selected files
          const { status, ...fileWithoutStatus } = f;
          return fileWithoutStatus;
        }
      }),
    );

    // Initialize the chat with file_id and file_type
    setChatInitializing(true);
    try {
      // Determine file type based on the file's type field
      const fileType = file.type === 'contract' ? 'contract' : 'plan_doc';
      await initialiseChatWithFile(file.id, fileType);

      // Update file status to success, keep other files without status
      setFiles((prev) =>
        prev.map((f) => {
          if (f.id === file.id) {
            return { ...f, status: 'success' };
          }
          return f;
        }),
      );
    } catch (error: any) {
      console.error('Error initializing chat:', error);
      // Update file status to error
      setFiles((prev) =>
        prev.map((f) => {
          if (f.id === file.id) {
            return { ...f, status: 'error' };
          }
          return f;
        }),
      );
    } finally {
      setChatInitializing(false);
    }

    // Load chat history
    try {
      const response = await getChatHistory(file.url);
      setChatMessages(response.data || []);
    } catch (error: any) {
      console.error('Error loading chat history:', error);
      setChatMessages([]);
    }
  };

  // Handle send message
  const handleSendMessage = async (message?: string) => {
    const userMessage = message?.trim();

    if (!userMessage) {
      return;
    }

    if (!selectedFile?.url) {
      return;
    }

    // Clear the input after sending
    setChatInputValue('');

    const fileUrl = selectedFile.url;
    const fileId = selectedFile.id;

    // Create user message
    const userMessageId = uuidv4();

    const newUserMessage: ChatMessage = {
      _id: userMessageId,
      file_url: fileUrl,
      file_id: fileId,
      message: userMessage,
      response: '',
      message_type: 'user',
      status: 'sent',
      createdAt: new Date().toISOString(),
    };

    // Add user message to chat immediately
    setChatMessages((prev) => [...prev, newUserMessage]);
    setIsThinking(true);
    setChatLoading(true);
    setStreamingResponse('');

    // Build messages array for LangGraph API
    const allMessages = [
      ...chatMessages.flatMap((msg) => {
        const msgs = [];
        if (msg.message) {
          msgs.push({
            id: `${msg._id}-user`,
            type: 'human' as const,
            content: msg.message,
          });
        }
        if (msg.response) {
          msgs.push({
            id: `${msg._id}-assistant`,
            type: 'ai' as const,
            content: msg.response,
          });
        }
        return msgs;
      }),
      {
        id: userMessageId,
        type: 'human' as const,
        content: userMessage,
      },
    ];

    try {
      // Include file metadata in the context
      const fileMetadata = {
        file_url: fileUrl,
        file_id: fileId,
        document_name: selectedFile.name,
        contract_id: contractStore.contractId || '',
        contract_type: selectedFile.type || '',
      };

      // Call wait endpoint (non-streaming)
      const response = await sendChatMessage(
        threadId,
        allMessages,
        fileMetadata,
      );

      // Extract the assistant's response
      const messages = response?.values?.messages || [];
      const lastMessage = messages[messages.length - 1];
      const responseContent = lastMessage?.content || 'No response received';

      // Extract suggestions from the response
      const suggestions = response?.values?.suggestions || [];
      setChatSuggestions(suggestions);

      // Update the existing user message with the response
      setChatMessages((prev) =>
        prev.map((msg) =>
          msg._id === userMessageId
            ? { ...msg, response: responseContent, status: 'completed' }
            : msg,
        ),
      );

      setIsThinking(false);
      setChatLoading(false);
    } catch (error: any) {
      console.error('Error sending message:', error);

      // Update the existing user message with error response
      setChatMessages((prev) =>
        prev.map((msg) =>
          msg._id === userMessageId
            ? {
                ...msg,
                response:
                  'I encountered an error processing your message. Please try again.',
                status: 'failed',
              }
            : msg,
        ),
      );
      setIsThinking(false);
      setChatLoading(false);
    }
  };

  // Handle file selection
  const handleFileSelect = async (fileIdOrObject: any) => {
    // FileListSidebar might pass file ID or file object
    let file: any = undefined;
    if (typeof fileIdOrObject === 'string') {
      // If it's a string, find the file by ID
      file = files.find((f) => f.id === fileIdOrObject);
    } else if (fileIdOrObject?.id) {
      // If it's an object with ID, use it directly
      file = fileIdOrObject;
    } else {
      console.error('Invalid file selection:', fileIdOrObject);
      return;
    }

    if (!file) {
      console.error('File not found:', fileIdOrObject);
      return;
    }

    // Create blob URL if not already created
    let updatedFile: any = file;
    if (file.originalUrl && !file.url) {
      try {
        const blobUrl = await createBlobUrl(file.originalUrl);
        updatedFile = { ...file, url: blobUrl };

        // Update the file in the files array
        setFiles((prevFiles: any[]) =>
          prevFiles.map((f: any) => (f.id === file.id ? updatedFile : f)),
        );
      } catch (error) {
        console.error('Error creating blob URL:', error);
        updatedFile = { ...file, url: file.originalUrl }; // Fallback to original URL
      }
    }

    setSelectedFile(updatedFile);
    setChatMessages([]);
    setChatSuggestions([]);
    await loadChatHistory(updatedFile);
  };

  // Resize handlers
  const handleMouseMoveLeft = (e: MouseEvent) => {
    if (!isResizingLeft) {
      return;
    }
    const newWidth = e.clientX - 60; // 60px is approximate sidebar width
    if (newWidth >= 200 && newWidth <= 500) {
      setLeftPanelWidth(newWidth);
    }
  };

  const handleMouseMoveRight = (e: MouseEvent) => {
    if (!isResizingRight) {
      return;
    }
    const newWidth = e.clientX - leftPanelWidth - 64; // Adjust for left panel and resize handle
    if (newWidth >= 300 && newWidth <= 800) {
      setMiddlePanelWidth(newWidth);
    }
  };

  const handleMouseUp = () => {
    setIsResizingLeft(false);
    setIsResizingRight(false);
  };

  useEffect(() => {
    if (!isResizingLeft && !isResizingRight) {
      return;
    }

    const handleMove = (e: MouseEvent) => {
      if (isResizingLeft) {
        handleMouseMoveLeft(e);
      }
      if (isResizingRight) {
        handleMouseMoveRight(e);
      }
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizingLeft, isResizingRight, leftPanelWidth]);

  if (loading) {
    return (
      <Grid className={classes.root}>
        <Loader />
      </Grid>
    );
  }

  // Format messages for the Chat component
  const formattedMessages: any[] = [];

  chatMessages.forEach((msg) => {
    // Add user message
    if (msg.message) {
      formattedMessages.push({
        id: `${msg._id}-user`,
        text: msg.message,
        isUser: true,
        timestamp: msg.createdAt ? new Date(msg.createdAt) : new Date(),
      });
    }
    // Add assistant response with Markdown component
    if (msg.response) {
      formattedMessages.push({
        id: `${msg._id}-assistant`,
        text: msg.response,
        isUser: false,
        timestamp: msg.createdAt ? new Date(msg.createdAt) : new Date(),
        component: <MarkdownMessage content={msg.response} />,
      });
    }
  });

  // Add streaming response if available
  if (streamingResponse && !isThinking) {
    formattedMessages.push({
      id: 'streaming',
      text: '',
      isUser: false,
      timestamp: new Date(),
      component: <MarkdownMessage content={streamingResponse} />,
    });
  }

  // Add thinking message if loading
  if (isThinking && !streamingResponse) {
    formattedMessages.push({
      id: 'thinking',
      text: 'Thinking...',
      isUser: false,
      timestamp: new Date(),
      isThinking: true,
    });
  }

  // Create a wrapped handler to ensure it's called
  const onFileSelectHandler = (file: any) => {
    handleFileSelect(file);
  };

  return (
    <Grid className={classes.root}>
      {/* Left Panel - File List */}
      <Grid
        className={classes.sidebar}
        style={{ width: `${leftPanelWidth}px` }}
      >
        <FileListSidebar
          files={files as any}
          selectedFiles={selectedFile?.id ? [selectedFile.id] : []}
          onFileSelect={onFileSelectHandler}
          statusTexts={{
            inprogress: 'In Progress',
            success: 'Ready',
            error: 'Error',
          }}
        />
      </Grid>

      {/* Resize Handle - Left */}
      <div
        className={classes.resizeHandle}
        onMouseDown={() => setIsResizingLeft(true)}
      />

      {/* Middle Panel - Document Preview */}
      <Grid
        className={classes.middleSection}
        style={{ width: `${middlePanelWidth}px` }}
      >
        {selectedFile ? (
          <>
            <Typography
              fontSize="14px"
              lineHeight="24px"
              fontType="semiBold"
              textColor={patientAccessTheme.palette.text.highemphasis}
              margin="16px 0px"
            >
              {selectedFile.name}
            </Typography>
            <Grid
              style={{
                marginTop: '36px',
              }}
            >
              {selectedFile.url !== '' && (
                <Worker workerUrl={PDF_WORKER_URL}>
                  <Viewer
                    fileUrl={selectedFile.url}
                    plugins={[highlightPluginInstance]}
                  />
                </Worker>
              )}
              {selectedFile.url === '' && (
                <NoDataVisual content="NO FILE FOUND" />
              )}
            </Grid>
          </>
        ) : (
          <div className={classes.placeholderText}>
            Select a document to preview
          </div>
        )}
      </Grid>

      {/* Resize Handle - Right */}
      <div
        className={classes.resizeHandle}
        onMouseDown={() => setIsResizingRight(true)}
      />

      {/* Right Panel - Chat */}
      <Grid className={classes.chatContainer}>
        <Chat
          key={chatKey}
          height="100%"
          headerTitle="Chat with AI"
          headerSubtitle={selectedFile?.name || 'Select a document to start'}
          disclaimerText="AI can make mistakes, please verify your answers"
          messages={formattedMessages}
          onSendMessage={handleSendMessage}
          suggestions={chatSuggestions}
          isGenerating={chatLoading}
          inputValue={chatInputValue}
          onInputChange={(value: string) => setChatInputValue(value)}
          onSuggestionClick={handleSuggestionClick}
        />
      </Grid>
    </Grid>
  );
});

export default ChatPage;
